/**
 * Tache 1292 — Relance groupee de la diarisation, avec le cout annonce AVANT.
 *
 * On ne lance jamais une depense sans l'avoir chiffree a l'ecran. Les videos
 * dont quelqu'un a DEMANDE un locuteur unique arrivent decochees : leur seul
 * locuteur n'est pas un defaut.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

export const DiarisationBatchButton = () => {
  // Tâche 1389 (Eric 2026-06-30) — Bouton resté français.
  const { tr } = useLang();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [running, setRunning] = useState(false);

  const charger = async () => {
    setLoading(true);
    setOpen(true);
    try {
      const { data: d } = await axios.get(`${API}/api/videos/diarisation/batch-candidates`,
        { withCredentials: true, timeout: 120000 });
      setData(d);
      const pre = {};
      (d.candidates || []).forEach((c) => { pre[c.video_id] = !c.explicit_single; });
      setSelected(pre);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Lecture impossible — réessayez.", "Could not read — try again."));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const ids = Object.keys(selected).filter((k) => selected[k]);
  const choisies = (data?.candidates || []).filter((c) => selected[c.video_id]);
  const minutes = Math.round(choisies.reduce((s, c) => s + (c.minutes || 0), 0) * 10) / 10;
  const usd = Math.round(minutes * (data?.rate_usd_per_minute || 0) * 100) / 100;

  const lancer = async () => {
    if (!ids.length) return;
    setRunning(true);
    try {
      const { data: r } = await axios.post(`${API}/api/videos/diarisation/batch-run`,
        { video_ids: ids }, { withCredentials: true, timeout: 600000 });
      toast.success(
        `${r.relaunched} vidéo(s) rediarisée(s) · ${r.minutes_processed} min · ≈ ${r.estimated_usd} USD`
        + (r.failed ? ` · ${r.failed} échec(s)` : ""),
        { duration: 12000 });
      setOpen(false);
      setData(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Relance impossible.");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <button
        onClick={charger}
        disabled={loading}
        className="px-3 py-2 rounded-lg border-2 border-blue-300 bg-white hover:bg-blue-50 text-blue-800 text-xs font-medium disabled:opacity-50"
        data-testid="diarisation-batch-btn"
        title={tr("Relancer la diarisation sur les vidéos qui n'ont qu'un seul locuteur, avec le coût annoncé avant", "Relaunch diarization on videos with a single speaker, with the cost stated up front")}
      >
        🎙 {loading ? tr("Lecture…", "Reading…") : tr("Relancer la diarisation par lot", "Relaunch diarization in bulk")}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4"
             onClick={() => !running && setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl"
               onClick={(e) => e.stopPropagation()} data-testid="diarisation-batch-modal">
            <div className="px-5 py-4 border-b border-zinc-200">
              <h3 className="text-base font-bold text-zinc-900">{tr("Relancer la diarisation", "Relaunch diarization")}</h3>
              <p className="text-xs text-zinc-600 mt-1">
                {tr("Vidéos dont la transcription ne porte qu'un seul locuteur. Celles où un locuteur unique a été demandé arrivent décochées.", "Videos whose transcript carries a single speaker. Those where a single speaker was requested arrive unticked.")}
              </p>
            </div>

            {data && (
              <div className="px-5 py-3 bg-amber-50 border-b border-amber-200" data-testid="diarisation-batch-cost">
                <div className="text-sm font-bold text-amber-900">
                  Coût estimé : ≈ {usd.toFixed(2)} USD
                </div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {choisies.length} vidéo(s) · {minutes} minute(s) d'audio ·
                  {" "}{data.rate_usd_per_minute} USD/minute (tarif AssemblyAI au {data.price_as_of})
                </div>
                <div className="text-xs text-amber-700 mt-1">{data.note}</div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1.5">
              {loading && <p className="text-sm text-zinc-500">{tr("Lecture des transcriptions…", "Reading the transcripts…")}</p>}
              {!loading && data && (data.candidates || []).length === 0 && (
                <p className="text-sm text-teal-700 font-medium" data-testid="diarisation-batch-empty">
                  {tr("Aucune vidéo à un seul locuteur — rien à relancer.", "No single-speaker video — nothing to relaunch.")}
                </p>
              )}
              {(data?.candidates || []).map((c) => (
                <label key={c.video_id}
                       className="flex items-start gap-3 p-2.5 rounded-lg border border-zinc-200 hover:bg-zinc-50 cursor-pointer">
                  <input type="checkbox" className="mt-1" checked={!!selected[c.video_id]}
                         onChange={(e) => setSelected((p) => ({ ...p, [c.video_id]: e.target.checked }))}
                         data-testid={`diarisation-check-${c.video_id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-zinc-800 font-medium break-words">{c.title || "(sans titre)"}</div>
                    <div className="text-xs text-zinc-500">
                      {c.minutes} min · locuteur « {c.speaker} »
                      {c.reason ? ` · ${c.reason}` : ""}
                      {c.explicit_single && " · locuteur unique demandé"}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="px-5 py-3 border-t border-zinc-200 flex flex-wrap items-center gap-2">
              <button onClick={() => setSelected({})}
                      className="px-3 py-2 rounded-lg border border-zinc-300 text-xs font-medium hover:bg-zinc-50"
                      data-testid="diarisation-batch-none">
                {tr("Tout décocher", "Untick all")}
              </button>
              <span className="flex-1" />
              <button onClick={() => setOpen(false)} disabled={running}
                      className="px-3 py-2 rounded-lg border border-zinc-300 text-xs font-medium hover:bg-zinc-50"
                      data-testid="diarisation-batch-cancel">
                {tr("Fermer sans lancer", "Close without launching")}
              </button>
              <button onClick={lancer} disabled={running || ids.length === 0}
                      className="px-4 py-2 rounded-lg bg-[#002FA7] hover:bg-blue-800 text-white text-xs font-bold disabled:opacity-40"
                      data-testid="diarisation-batch-run">
                {running ? "Diarisation…" : `Lancer (${ids.length}) — ≈ ${usd.toFixed(2)} USD`}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default DiarisationBatchButton;
